import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, message, Popconfirm, DatePicker, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import apiClient from '../services/apiConfig';

interface Notification {
  id: number;
  type: number;
  title: string;
  description: string;
  expire_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/notifications/all');
      setNotifications(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 401) {
        message.error('Sesión expirada. Por favor inicia sesión nuevamente.');
      } else {
        message.error('Error al cargar las notificaciones');
      }
    } finally {
      setLoading(false);
    }
  };

  const showModal = (notification?: Notification) => {
    setIsModalOpen(true);
    if (notification) {
      setCurrentNotification(notification);
      form.setFieldsValue({
        ...notification,
        expire_at: notification.expire_at ? dayjs(notification.expire_at) : null,
      });
    } else {
      setCurrentNotification(null);
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        expire_at: values.expire_at ? values.expire_at.format('YYYY-MM-DD HH:mm:ss') : null,
      };

      if (currentNotification) {
        await apiClient.put(`/notifications/${currentNotification.id}`, payload);
        message.success('Notificación actualizada');
      } else {
        await apiClient.post('/notifications', payload);
        message.success('Notificación creada');
      }

      setIsModalOpen(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        message.error('Sesión expirada. Por favor inicia sesión nuevamente.');
      } else {
        message.error('Error al guardar la notificación');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      message.success('Notificación eliminada');
      fetchNotifications();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        message.error('Sesión expirada. Por favor inicia sesión nuevamente.');
      } else {
        message.error('Error al eliminar la notificación');
      }
    }
  };

  const handleToggleActive = async (notification: Notification) => {
    try {
      await apiClient.post(`/notifications/${notification.id}/toggle`);
      message.success('Estado actualizado');
      fetchNotifications();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        message.error('Sesión expirada. Por favor inicia sesión nuevamente.');
      } else {
        message.error('Error al actualizar el estado');
      }
    }
  };

  const getTypeLabel = (type: number) => {
    const types = {
      1: { label: 'General', color: 'blue' },
      2: { label: 'Descuento/Promoción', color: 'green' },
      3: { label: 'Regalo/Recompensa', color: 'gold' },
    };
    return types[type as keyof typeof types] || { label: 'Desconocido', color: 'default' };
  };

  const columns: ColumnsType<Notification> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 180,
      render: (type: number) => {
        const { label, color } = getTypeLabel(type);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Vence',
      dataIndex: 'expire_at',
      key: 'expire_at',
      width: 150,
      render: (date: string | null) => date ? dayjs(date).format('DD/MM/YYYY') : 'Sin vencimiento',
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean, record: Notification) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Activa"
          unCheckedChildren="Inactiva"
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar notificación?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Notificaciones</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Nueva Notificación
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={notifications}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={currentNotification ? 'Editar Notificación' : 'Nueva Notificación'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        width={600}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="type"
            label="Tipo de Notificación"
            rules={[{ required: true, message: 'Selecciona el tipo de notificación' }]}
          >
            <Select placeholder="Selecciona un tipo">
              <Select.Option value={1}>General</Select.Option>
              <Select.Option value={2}>Descuento/Promoción</Select.Option>
              <Select.Option value={3}>Regalo/Recompensa</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Título"
            rules={[{ required: true, message: 'Ingresa el título' }]}
          >
            <Input placeholder="Ej: Nueva promoción disponible" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: 'Ingresa la descripción' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Ej: Aprovecha el 15% de descuento en masajes durante todo octubre"
            />
          </Form.Item>

          <Form.Item
            name="expire_at"
            label="Fecha de Vencimiento (Opcional)"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
              showTime={{ format: 'HH:mm' }}
              placeholder="Selecciona fecha y hora"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Estado"
            valuePropName="checked"
          >
            <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationsPage;
